import { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import { PageShell } from '../../components';
import { emailConfigService } from '../../services/emailConfigService';
import { EmailConfigInput, EmailProvider } from '../../types/emailConfig';

interface Preset {
  host: string;
  port: number;
  secure: boolean;
}

const PROVIDER_PRESETS: Record<EmailProvider, Preset | null> = {
  gmail: { host: 'smtp.gmail.com', port: 465, secure: true },
  outlook: { host: 'smtp.office365.com', port: 587, secure: false },
  custom: null,
};

interface FormState {
  provider: EmailProvider;
  host: string;
  port: number;
  secure: boolean;
  fromEmail: string;
  fromName: string;
  username: string;
  password: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  provider: 'custom',
  host: '',
  port: 587,
  secure: false,
  fromEmail: '',
  fromName: '',
  username: '',
  password: '',
  isActive: true,
};

export function EmailConfigPage() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [hasPassword, setHasPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    void loadConfig();
  }, []);

  async function loadConfig(): Promise<void> {
    setLoading(true);
    try {
      const config = await emailConfigService.get();
      if (config) {
        setForm({
          provider: config.provider,
          host: config.host,
          port: config.port,
          secure: config.secure,
          fromEmail: config.fromEmail,
          fromName: config.fromName,
          username: config.username,
          password: '', // write-only: nunca pré-preenchido
          isActive: config.isActive,
        });
        setHasPassword(config.hasPassword);
      }
    } catch (err: unknown) {
      setSnack({
        open: true,
        message: err instanceof Error ? err.message : 'Falha ao carregar a configuração de e-mail',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }

  function handleProviderChange(e: SelectChangeEvent<EmailProvider>): void {
    const provider = e.target.value as EmailProvider;
    const preset = PROVIDER_PRESETS[provider];
    setForm(prev => ({
      ...prev,
      provider,
      ...(preset ? { host: preset.host, port: preset.port, secure: preset.secure } : {}),
    }));
  }

  function handleText(field: keyof FormState) {
    return (e: React.ChangeEvent<HTMLInputElement>): void => {
      const value = field === 'port' ? Number(e.target.value) : e.target.value;
      setForm(prev => ({ ...prev, [field]: value }));
    };
  }

  function buildInput(): EmailConfigInput {
    const input: EmailConfigInput = {
      provider: form.provider,
      host: form.host,
      port: form.port,
      secure: form.secure,
      fromEmail: form.fromEmail,
      fromName: form.fromName,
      username: form.username,
      isActive: form.isActive,
    };
    if (form.password.length > 0) {
      input.password = form.password;
    }
    return input;
  }

  async function handleTest(): Promise<void> {
    setTesting(true);
    try {
      const result = await emailConfigService.test(buildInput());
      setSnack({
        open: true,
        message: result.success
          ? 'Conexão SMTP bem-sucedida!'
          : `Falha na conexão: ${result.error ?? 'erro desconhecido'}`,
        severity: result.success ? 'success' : 'error',
      });
    } catch (err: unknown) {
      setSnack({
        open: true,
        message: err instanceof Error ? err.message : 'Falha ao testar a conexão',
        severity: 'error',
      });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave(): Promise<void> {
    setSaving(true);
    try {
      const saved = await emailConfigService.save(buildInput());
      setHasPassword(saved.hasPassword);
      setForm(prev => ({ ...prev, password: '' }));
      setSnack({ open: true, message: 'Configuração salva com sucesso!', severity: 'success' });
    } catch (err: unknown) {
      setSnack({
        open: true,
        message: err instanceof Error ? err.message : 'Falha ao salvar a configuração',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  }

  const isCustom = form.provider === 'custom';

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/home' },
        { label: 'Administrativo' },
        { label: 'Configuração de E-mail' },
      ]}
      title='Configuração de E-mail (SMTP)'
      subtitle='Conta de envio usada pelo sistema. A senha é armazenada criptografada e nunca é exibida.'
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 3, maxWidth: 700 }}>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel id='provider-label'>Provedor</InputLabel>
              <Select<EmailProvider>
                labelId='provider-label'
                label='Provedor'
                value={form.provider}
                onChange={handleProviderChange}
              >
                <MenuItem value='gmail'>Gmail</MenuItem>
                <MenuItem value='outlook'>Outlook / Microsoft 365</MenuItem>
                <MenuItem value='custom'>Outro (SMTP personalizado)</MenuItem>
              </Select>
            </FormControl>

            {form.provider === 'gmail' && (
              <Alert severity='info'>
                Para o Gmail, use uma <strong>Senha de App</strong> do Google (requer verificação em
                2 etapas ativada na conta).
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label='Servidor SMTP (host)'
                value={form.host}
                onChange={handleText('host')}
                fullWidth
                disabled={!isCustom}
              />
              <TextField
                label='Porta'
                type='number'
                value={form.port}
                onChange={handleText('port')}
                sx={{ width: { sm: 140 } }}
                disabled={!isCustom}
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={form.secure}
                  onChange={e => setForm(prev => ({ ...prev, secure: e.target.checked }))}
                  disabled={!isCustom}
                />
              }
              label='Conexão segura (SSL/TLS direto — porta 465). Desmarque para STARTTLS (587).'
            />

            <Divider />

            <TextField
              label='E-mail do remetente'
              type='email'
              value={form.fromEmail}
              onChange={handleText('fromEmail')}
              fullWidth
            />
            <TextField
              label='Nome do remetente'
              value={form.fromName}
              onChange={handleText('fromName')}
              fullWidth
            />
            <TextField
              label='Usuário SMTP'
              value={form.username}
              onChange={handleText('username')}
              fullWidth
            />
            <TextField
              label='Senha / Senha de App'
              type='password'
              value={form.password}
              onChange={handleText('password')}
              placeholder={hasPassword ? '•••••••• (deixe em branco para manter)' : ''}
              autoComplete='new-password'
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={e => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label='Conta ativa (usar esta conta para enviar e-mails)'
            />

            <Stack direction='row' spacing={2} sx={{ pt: 1 }}>
              <Button
                variant='outlined'
                onClick={() => void handleTest()}
                disabled={testing || saving}
              >
                {testing ? 'Testando…' : 'Testar conexão'}
              </Button>
              <Button
                variant='contained'
                onClick={() => void handleSave()}
                disabled={saving || testing}
              >
                {saving ? 'Salvando…' : 'Salvar'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
}

export default EmailConfigPage;
